/**
 * VerificationsOverview — the Verifications landing panel.
 *
 * Work and contractor verifications are separate flows (different tables,
 * statuses and units of work), so a merged list would be misleading. This
 * reports each on its own terms and points into the right list, leading with
 * whatever needs an admin.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Alert,
  Skeleton,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Assignment,
  Engineering,
  ChevronRight,
  ErrorOutline,
  CheckCircleOutline,
} from '@mui/icons-material';

import adminService from '../../services/adminService';
import { panelSx, panelHeaderSx } from './surfaces';
import { brand, status as statusTokens, text, border } from '../../theme/colors';

const titleise = (value) => String(value || '').replace(/_/g, ' ');

const STATUS_TONES = {
  pending: statusTokens.warning,
  assigned: statusTokens.info,
  accepted: statusTokens.info,
  in_progress: statusTokens.info,
  submitted: statusTokens.warning,
  changes_requested: statusTokens.warning,
  verification_completed: statusTokens.success,
  approved: statusTokens.success,
  completed: statusTokens.success,
  cancelled: statusTokens.neutral,
};

/**
 * A headline figure inside a flow card. Every figure is a link into the list
 * filtered to exactly the rows it counts — 'open' and 'done' are server-side
 * status groups, so the count and the filtered list always agree.
 */
const Stat = ({ label, value, tone, onClick }) => (
  <Grid item xs={4}>
    <Box
      component={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      sx={{
        textAlign: 'left',
        width: '100%',
        border: 'none',
        bgcolor: 'transparent',
        p: 0,
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: 1,
        '&:hover .stat-value': onClick ? { color: brand.primary } : {},
        '&:hover .stat-label': onClick ? { color: text.secondary } : {},
      }}
    >
      <Typography
        variant="caption"
        className="stat-label"
        sx={{
          color: text.tertiary,
          fontWeight: 600,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
          display: 'block',
        }}
      >
        {label}
      </Typography>
      <Typography
        className="stat-value"
        sx={{
          fontSize: 24,
          fontWeight: 700,
          color: tone || text.primary,
          lineHeight: 1.2,
          transition: 'color 140ms',
        }}
      >
        {value}
      </Typography>
    </Box>
  </Grid>
);

const FlowCard = ({ icon, title, blurb, data, callout, onOpen, go }) => {
  const total = data?.total || 0;
  const byStatus = data?.byStatus || {};
  const liveShare = total ? Math.round(((data.open || 0) / total) * 100) : 0;

  return (
    <Grid item xs={12} md={6}>
      <Paper elevation={0} sx={{ ...panelSx, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ ...panelHeaderSx, display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: `${brand.primary}14`,
              color: brand.primary,
              '& svg': { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: text.primary }}>{title}</Typography>
            <Typography variant="caption" sx={{ color: text.tertiary }}>{blurb}</Typography>
          </Box>
        </Box>

        <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Grid container spacing={2}>
            <Stat label="Total" value={total} onClick={() => go()} />
            <Stat
              label="Open"
              value={data?.open || 0}
              tone={statusTokens.infoDark}
              onClick={() => go({ status: 'open' })}
            />
            <Stat
              label="Cancelled"
              value={data?.cancelled || 0}
              tone={text.tertiary}
              onClick={() => go({ status: 'cancelled' })}
            />
          </Grid>

          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={liveShare}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: border.light,
                '& .MuiLinearProgress-bar': { bgcolor: statusTokens.info, borderRadius: 3 },
              }}
            />
            <Typography variant="caption" sx={{ color: text.tertiary, mt: 0.5, display: 'block' }}>
              {liveShare}% still open
            </Typography>
          </Box>

          {callout && (
            <Alert
              severity={callout.count > 0 ? 'warning' : 'success'}
              icon={callout.count > 0 ? <ErrorOutline sx={{ fontSize: 18 }} /> : <CheckCircleOutline sx={{ fontSize: 18 }} />}
              sx={{ mt: 2, borderRadius: 2, py: 0.25 }}
              action={
                callout.count > 0 ? (
                  <Button color="inherit" size="small" onClick={callout.onOpen} sx={{ textTransform: 'none' }}>
                    Show
                  </Button>
                ) : null
              }
            >
              {callout.count > 0 ? callout.label : callout.clearLabel}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="caption"
            sx={{ color: text.tertiary, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}
          >
            By status
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
            {Object.keys(byStatus).length === 0 ? (
              <Typography variant="body2" sx={{ color: text.tertiary }}>Nothing recorded yet.</Typography>
            ) : (
              Object.entries(byStatus)
                .sort((a, b) => b[1] - a[1])
                .map(([st, count]) => {
                  const tone = STATUS_TONES[st] || statusTokens.neutral;
                  return (
                    <Chip
                      key={st}
                      size="small"
                      clickable
                      onClick={() => go({ status: st })}
                      label={`${titleise(st)} · ${count}`}
                      sx={{
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        fontSize: 11,
                        height: 24,
                        color: tone,
                        bgcolor: `${tone}1a`,
                        '&:hover': { bgcolor: `${tone}2e` },
                      }}
                    />
                  );
                })
            )}
          </Box>

          <Box sx={{ mt: 'auto', pt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              endIcon={<ChevronRight />}
              onClick={onOpen}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              View all
            </Button>
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
};

const VerificationsOverview = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminService.getVerificationSummary());
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not load the summary.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const go = (kind, extra) => {
    const params = new URLSearchParams({ kind, ...(extra || {}) });
    navigate(`/admin/home/verifications?${params.toString()}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: text.primary, mb: 0.25 }}>
        Verifications
      </Typography>
      <Typography variant="body2" sx={{ color: text.secondary, mb: 2.5 }}>
        Two separate flows — pick one to work through.
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2.5, borderRadius: 2 }}
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Grid container spacing={2.5}>
          {[0, 1].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rounded" height={340} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2.5} alignItems="stretch">
          <FlowCard
            icon={<Assignment />}
            title="Work verifications"
            blurb="Project & milestone checks, plus standalone property checks"
            data={data?.work}
            go={(extra) => go('work', extra)}
            callout={{
              count: data?.work?.unassigned || 0,
              label: `${data?.work?.unassigned} awaiting a verifier`,
              clearLabel: 'Everything live has a verifier assigned',
              onOpen: () => go('work', { assigned: 'no' }),
            }}
            onOpen={() => go('work')}
          />
          <FlowCard
            icon={<Engineering />}
            title="Contractor verifications"
            blurb="Contractor vetting files and who is working them"
            data={data?.contractor}
            go={(extra) => go('contractor', extra)}
            callout={{
              count: data?.contractor?.unassigned || 0,
              label: `${data?.contractor?.unassigned} awaiting a verifier`,
              clearLabel: 'Every contractor file has a verifier assigned',
              onOpen: () => go('contractor', { assigned: 'no' }),
            }}
            onOpen={() => go('contractor')}
          />
        </Grid>
      )}
    </Box>
  );
};

export default VerificationsOverview;
